import React, { useEffect, useMemo, useState, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { CHINESE_NEW_YEARS } from 'common/src/utils/definitions';
import { RMB_TO_USD } from '../../utils/definitions';
import useDataStore from '../../stores/useDataStore';
import useUserStore from '../../stores/useUserStore';
import { getChartThemeColors, tooltipContainerStyle, tooltipTitleStyle, tooltipLabelStyle, tooltipValueStyle } from '../../utils/chartTheme';
import { useTranslation } from '../../hooks/useTranslation';
import { Spinner } from '@nextui-org/react';
import { lineShadow, showIsolatedPoints } from './isolatedPoints';

interface ChineseChartProps {
  series: ApexAxisChartSeries;
  xaxisCategories: string[];
  height?: number;
  yaxisTitle: string;
  priceView: 'kg' | 'box';
  selectedCurrency: string;
  tooltipCustom?: (obj: {
    seriesIndex: number;
    dataPointIndex: number;
    w: any;
  }) => string;
}

const ChineseChart: React.FC<ChineseChartProps> = ({
  series,
  xaxisCategories,
  height = 350,
  yaxisTitle,
  priceView,
  selectedCurrency,
  tooltipCustom,
}) => {
  const { t } = useTranslation('market');
  const { darkMode } = useUserStore();
  const chartColors = getChartThemeColors(darkMode);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedSeasonsMarketData, loadingMarketData } = useDataStore();
  const isInitialLoad = useRef(true);
  const calculationTimeout = useRef<NodeJS.Timeout>();

  // Memoize the color conversion functions
  const hslToHex = useMemo(() => {
    return (h: number, s: number, l: number) => {
      s /= 100;
      l /= 100;
      const k = (n: number) => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = (n: number) =>
        l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return (
        '#' +
        [255 * f(0), 255 * f(8), 255 * f(4)]
          .map((x) => Math.round(x).toString(16).padStart(2, '0'))
          .join('')
      );
    };
  }, []);

  const lightenColor = useMemo(() => {
    return (color: string, factor: number) => {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      const lightenChannel = (c: number) => Math.round(c + (255 - c) * factor);

      return `#${[r, g, b]
        .map((c) => lightenChannel(c).toString(16).padStart(2, '0'))
        .join('')}`;
    };
  }, []);

  // Memoize the data transformation
  const transformedData = useMemo(() => {
    // Crear un Map para cachear los cálculos de color
    const colorCache = new Map();

    const weekRanges = Array.from({ length: 22 }, (_, i) => i - 18);
    const seasons = selectedSeasonsMarketData.reduce((acc, season) => {
      const reference = CHINESE_NEW_YEARS[`season${season.split('-')[1]}`];
      // Solo incluir temporadas que tienen fecha de año nuevo chino definida
      if (reference) {
        acc[season] = {
          reference,
          series: [],
        };
      }
      return acc;
    }, {} as Record<string, { reference: Date; series: any[] }>);

    // Procesar los datos en chunks para mejorar el rendimiento
    return series.flatMap((s) => {
      // Usar el caché de colores
      let hexColor = colorCache.get(s.color);
      if (!hexColor) {
        const baseColor = s.color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
        hexColor = baseColor
          ? hslToHex(
            Number(baseColor[1]),
            Number(baseColor[2]),
            Number(baseColor[3])
          )
          : '#000000';
        colorCache.set(s.color, hexColor);
      }

      return Object.entries(seasons).map(([seasonKey, seasonData]) => {
        // Pre-calcular las fechas relativas
        const dateMap = new Map();
        s.data.forEach((point) => {
          const currentDate = new Date(point.x);
          const diffTime =
            currentDate.getTime() - seasonData.reference.getTime();
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
          const weekNumber = Math.floor(diffDays / 7);

          // Aplicar conversiones de una vez
          let value = priceView === 'box' ? point.y * 5 : point.y;
          value = selectedCurrency === 'USD' ? RMB_TO_USD(value) : value;

          if (!dateMap.has(weekNumber)) {
            dateMap.set(weekNumber, []);
          }
          dateMap.get(weekNumber).push(value);
        });

        // Generar los datos semanales - mapear a índices de categorías
        const weeklyData = weekRanges.map((weekNum, index) => {
          const values = dateMap.get(weekNum) || [];
          const weekAverage =
            values.length > 0
              ? values.reduce((sum, val) => sum + val, 0) / values.length
              : null;

          return {
            x: `Week ${weekNum > 0 ? '+' : ''}${weekNum}`, // String que coincide con las categorías
            y: weekAverage,
          };
        });

        return {
          name: `${s.name} (${seasonKey})`,
          data: weeklyData,
          color: (() => {
            // Define distinct styles for each season - using color variations
            const seasonStyleMap: Record<string, { hueOffset: number; saturation: number; lightness: number }> = {
              '2021-2022': { hueOffset: 180, saturation: 70, lightness: 35 },
              '2022-2023': { hueOffset: 120, saturation: 75, lightness: 40 },
              '2023-2024': { hueOffset: -120, saturation: 75, lightness: 45 },
              '2024-2025': { hueOffset: 60, saturation: 80, lightness: 50 },
              '2025-2026': { hueOffset: 0, saturation: 85, lightness: 55 },
              '2026-2027': { hueOffset: -60, saturation: 80, lightness: 45 },
            };

            const baseColor = hexColor.match(/#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
            if (!baseColor) return hexColor;

            // Convert hex to HSL
            const r = parseInt(baseColor[1], 16) / 255;
            const g = parseInt(baseColor[2], 16) / 255;
            const b = parseInt(baseColor[3], 16) / 255;

            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            let h = 0;

            if (max !== min) {
              const d = max - min;
              if (max === r) {
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
              } else if (max === g) {
                h = ((b - r) / d + 2) / 6;
              } else {
                h = ((r - g) / d + 4) / 6;
              }
            }

            const seasonStyle = seasonStyleMap[seasonKey] || { hueOffset: 0, saturation: 70, lightness: 50 };
            const newHue = ((h * 360) + seasonStyle.hueOffset + 360) % 360;
            return hslToHex(newHue, seasonStyle.saturation, seasonStyle.lightness);
          })(),
        };
      });
    });
  }, [series, hslToHex, lightenColor, priceView, selectedCurrency]);

  // Series con una sola semana con datos: como puntos (ver isolatedPoints.ts).
  const chartSeries = useMemo(
    () => showIsolatedPoints(transformedData),
    [transformedData]
  );

  // Effect para manejar el loading
  useEffect(() => {
    // Solo mostrar loading en la carga inicial
    if (!isInitialLoad.current) {
      return;
    }

    setIsLoading(true);

    if (calculationTimeout.current) {
      clearTimeout(calculationTimeout.current);
    }

    calculationTimeout.current = setTimeout(() => {
      if (
        transformedData.length > 0 &&
        transformedData.some(
          (series) =>
            series.data.length > 0 &&
            series.data.some((point) => point.y !== null)
        )
      ) {
        setIsLoading(false);
        isInitialLoad.current = false;
      }
    }, 100);

    return () => {
      if (calculationTimeout.current) {
        clearTimeout(calculationTimeout.current);
      }
    };
  }, [transformedData]);

  // Generar categories como array de strings
  const xAxisCategories = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => {
        const weekNum = i - 18;
        return `Week ${weekNum > 0 ? '+' : ''}${weekNum}`;
      }),
    []
  );

  // Memoize the chart options
  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        height: height,
        type: 'line',
        toolbar: { show: false },
        foreColor: chartColors.foreColor,
        dropShadow: {
          ...lineShadow(chartSeries),
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 3,
        curve: 'smooth',
      },
      grid: {
        borderColor: chartColors.gridBorderColor,
        row: {
          colors: chartColors.gridRowColors,
          opacity: 0.5,
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: xAxisCategories,
        title: {
          text: 'Weeks from Chinese New Year',
        },
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: true,
        },
      },
      yaxis: {
        title: {
          text: yaxisTitle,
        },
        labels: {
          formatter: (value) =>
            `${Math.round(value)} ${selectedCurrency === 'RMB' ? '¥' : '$'}`,
        },
      },
      tooltip: {
        shared: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          // Obtener la categoría directamente del índice
          const weekLabel = xAxisCategories[dataPointIndex] || 'N/A';

          let content = `
            <div style="${tooltipContainerStyle(darkMode)}">
              <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 8px;">
                ${weekLabel} from Chinese New Year
              </div>
          `;

          const sortedSeriesData = w.globals.seriesNames
            .map((name: string, index: number) => ({
              name,
              value: w.globals.series[index][dataPointIndex],
              color: w.globals.colors[index],
            }))
            .filter(({ value }: any) => value !== null && !isNaN(value))
            .sort((a: any, b: any) => b.value - a.value);

          sortedSeriesData.forEach(({ name, value, color }: any) => {
            content += `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <div style="display: flex; align-items: center;">
                  <span style="
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    background-color: ${color};
                    border-radius: 50%;
                    margin-right: 8px;
                  "></span>
                  <span style="color: ${chartColors.tooltipText};">${name}:</span>
                </div>
                <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">
                  ${typeof value === 'number' ? value.toFixed(2) : 'N/A'}
                  ${selectedCurrency}/${priceView === 'kg' ? 'Kg' : '5kg box'}
                </span>
              </div>
            `;
          });

          content += '</div>';
          return content;
        },
      },
      markers: {
        size: 5,
      },
      legend: {
        position: 'bottom',
      },
      annotations: {
        xaxis: [
          {
            x: 'Week 0', // String que coincide con la categoría
            borderColor: '#FF4560',
            label: {
              borderColor: '#FF4560',
              style: {
                color: '#fff',
                background: '#FF4560',
              },
              text: 'Chinese New Year',
            },
          },
        ],
      },
    }),
    [height, yaxisTitle, selectedCurrency, tooltipCustom, priceView, transformedData, chartSeries, xAxisCategories]
  );

  if (loadingMarketData) {
    return (
      <div className='w-full h-[350px] flex items-center justify-center bg-default-50 rounded-lg'>
        <div className='flex flex-col items-center gap-2'>
          <Spinner />
          <span className='text-sm text-default-500'>
            {t('Procesando datos históricos...')}
          </span>
          <p className='text-sm text-default-500'>
            {t('Este proceso puede tardar unos minutos. Estamos recopiando todos los datos de las temporadas seleccionadas.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ReactApexChart
      options={options}
      series={chartSeries as ApexAxisChartSeries}
      type='line'
      height={height}
    />
  );
};

export default React.memo(ChineseChart); // Prevent unnecessary re-renders
